import { useState } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  Megaphone,
  Target,
  Users,
  LayoutGrid,
  Radio,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  Settings,
  ShoppingBag,
  CheckCircle2,
  Clock,
  Truck,
  CalendarCheck,
  Bell,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import type { Locale } from 'date-fns'
import { th as thLocale } from 'date-fns/locale'
import { usePageTranslation } from '@/i18n/useTranslation'
import { useLanguage } from '@/i18n/useLanguage'
import { lineOverviewTranslations as enT } from '@/i18n/locales/en/lineOverview'
import { lineOverviewTranslations as thT } from '@/i18n/locales/th/lineOverview'
import { LineOAStoreSwitcher } from './LineOAStoreSwitcher'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import { useLineOADashboard } from './useLineOADashboard'
import { useLineOASparkline } from './useLineOASparkline'
import {
  useLineOABroadcastQuota,
  useLineOABroadcastHistory,
} from './useLineOABroadcast'
import { usePaymentReminderStats } from './usePaymentReminderStats'
import { useLineNotificationActivity } from './useLineNotificationActivity'
import { useLineCampaignHistory } from './useLineCampaign'
import { CampaignSentHistory } from './CampaignSentHistory'
import type { PulsePeriod } from '@/hooks/usePulseSparkline'
import type { LineOASparklinePoint } from '@/lib/api/controlPlaneClient'

// LINE brand green (this page deliberately matches LINE Official Account Manager)
const GREEN = '#06C755'

// LINE brand mark
function LineIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  )
}

function fmtDate(d: string) {
  // "2026-08-04" -> "08/04"
  const parts = (d || '').split('-')
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d
}

// Small SVG line chart matching LINE's insight feel
function Sparkline({ points, color = GREEN }: { points: LineOASparklinePoint[]; color?: string }) {
  if (!points || points.length < 2) return null
  const w = 100
  const h = 40
  const vals = points.map((p) => p.value)
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const range = max - min || 1
  const step = w / (points.length - 1)
  const line = points
    .map((p, i) => `${i * step},${h - ((p.value - min) / range) * (h - 4) - 2}`)
    .join(' ')
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-10">
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

// Change chip: green up / red down
function Change({ delta }: { delta: number }) {
  if (delta === 0) return <span className="text-black/50">0</span>
  const up = delta > 0
  return (
    <span className={up ? 'text-[#06C755] font-semibold' : 'text-red-500 font-semibold'}>
      {up ? '+' : ''}{delta.toLocaleString()}
    </span>
  )
}

const PERIODS: { key: PulsePeriod; tKey: 'period7d' | 'period30d' | 'period90d' }[] = [
  { key: '7d', tKey: 'period7d' },
  { key: '30d', tKey: 'period30d' },
  { key: '90d', tKey: 'period90d' },
]

// LINE-style hero action card (title, description, illustration, button)
function HeroCard({
  title,
  description,
  to,
  cta,
  icon: Icon,
}: {
  title: string
  description: string
  to: string
  cta: string
  icon: typeof Megaphone
}) {
  return (
    <div className="flex-1 card rounded-[5px] border border-black/[0.125] bg-white p-4 flex flex-col">
      <div className="flex-1 flex">
        <div className="flex-1">
          <h3 className="text-[1.05rem] font-bold text-[#353a40] mb-1">{title}</h3>
          <p className="text-black/50 text-sm">{description}</p>
        </div>
        <div className="flex-shrink-0 ml-2 w-14 h-14 rounded-full bg-[#06C755]/10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#06C755]" />
        </div>
      </div>
      <div className="mt-3">
        <Link
          to={to}
          className="inline-block rounded-[5px] bg-[#06C755] hover:bg-[#06C755]/80 text-white font-semibold text-sm px-3 py-2 transition-colors"
        >
          {cta}
        </Link>
      </div>
    </div>
  )
}

// Automated notifications shown in the dashboard activity box.
// Labels/descriptions come from i18n (notif_<key> / notif_<key>_desc).
const NOTIF_META: {
  key: string
  icon: typeof Bell
  enabledKey: string | null // null = always on
}[] = [
  { key: 'order_confirmation', icon: ShoppingBag, enabledKey: 'enable_line_order_confirmation' },
  { key: 'payment_confirmation', icon: CheckCircle2, enabledKey: 'enable_line_payment_confirmation' },
  { key: 'payment_reminder', icon: Clock, enabledKey: 'enable_line_payment_reminder' },
  { key: 'shipment', icon: Truck, enabledKey: 'enable_line_shipment_notification' },
  { key: 'booking_confirmation', icon: CalendarCheck, enabledKey: null },
]

function relTime(s?: string | null, locale?: Locale) {
  if (!s) return null
  try {
    return formatDistanceToNow(new Date(s.replace(' ', 'T')), { addSuffix: true, locale })
  } catch {
    return null
  }
}

export function LineOAOverviewPage() {
  const t = usePageTranslation({ en: enT, th: thT })
  const { language } = useLanguage()
  const dfLocale = language === 'th' ? thLocale : undefined
  const [period, setPeriod] = useState<PulsePeriod>('30d')
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()
  const connected = !!status?.connected

  const { data: dashboard, isLoading: loadingDash, isError, refetch } = useLineOADashboard({ enabled: connected })
  const { data: spark, isLoading: loadingSpark } = useLineOASparkline(period, connected)
  const { data: quota } = useLineOABroadcastQuota({ enabled: connected })
  const { data: history } = useLineOABroadcastHistory({ enabled: connected })
  const { data: reminderStats } = usePaymentReminderStats({ enabled: connected })
  const { data: notifActivity } = useLineNotificationActivity({ enabled: connected })
  const { data: campaignHistory } = useLineCampaignHistory(connected)

  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  if (!connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">{t.title}</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[#06C755]/10 flex items-center justify-center mx-auto">
                <LineIcon className="w-6 h-6 text-[#06C755]" />
              </div>
              <div>
                <p className="font-medium">{t.connectTitle}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t.connectDesc}
                </p>
              </div>
              <Button asChild className="bg-[#06C755] hover:bg-[#06C755]/80">
                <Link to="/storefront/settings/line">{t.connectCta}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  // Build the Trends metric rows from the sparkline series.
  // Change = end − start so it always matches the two visible date columns
  // (the summary.*_delta fields are period-over-period, which confusingly differ
  // from the start→end diff the table shows).
  const trendRows = spark
    ? [
        { label: t.metricFriends, series: spark.followers },
        { label: t.metricTargetReach, series: spark.targeted_reach },
        { label: t.metricNewFollowers, series: spark.new_followers },
      ].filter((r) => r.series && r.series.length > 0)
    : []
  const startDate = spark?.followers?.[0]?.date
  const endDate = spark?.followers?.[spark.followers.length - 1]?.date

  // Targeted-campaign stats derived from the same send history the list shows.
  const campaignSends = campaignHistory?.history ?? []
  const campaignTotalSends = campaignSends.length
  const campaignTotalDelivered = campaignSends.reduce((sum, r) => sum + (r.recipient_count || 0), 0)
  const campaignSuccessful = campaignSends.filter((r) => r.status === 'sent').length

  return (
    <PageContainer>
      <div className="p-6 space-y-4 max-w-5xl">
        {/* OA switcher — above the profile box */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[#353a40]">{t.switchOA}</span>
          <LineOAStoreSwitcher />
        </div>

        {/* Profile header (LINE-style) */}
        <div className="rounded-[10px] border border-black/[0.08] bg-white p-4 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {status?.bot_picture_url ? (
                <img
                  src={status.bot_picture_url}
                  alt={status.bot_display_name || 'LINE OA'}
                  className="w-16 h-16 rounded-full border border-black/10"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#06C755] flex items-center justify-center">
                  <LineIcon className="w-8 h-8 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 ml-3 min-w-0">
              <h2 className="text-[1.4rem] font-bold text-[#353a40] truncate">
                {status?.bot_display_name || 'LINE OA'}
              </h2>
              <div className="flex items-center gap-1 text-black/50">
                <Users className="w-4 h-4" />
                <span className="font-semibold text-[#353a40]">
                  {dashboard?.followers != null ? dashboard.followers.toLocaleString() : '—'}
                </span>
                <span className="text-muted-foreground text-sm ml-1">{t.friends}</span>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <Button
                asChild
                variant="outline"
                size="icon"
                className="border-black/15 text-[#353a40] hover:bg-black/5"
                title={t.lineSettings}
              >
                <Link to="/storefront/settings/line">
                  <Settings className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {isError && (
          <div className="flex items-center gap-3 p-4 rounded bg-red-50 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm flex-1">{t.loadError}</p>
            <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-red-700 hover:bg-red-100">
              <RefreshCw className="w-4 h-4 mr-1.5" /> {t.retry}
            </Button>
          </div>
        )}

        {/* Hero action cards */}
        <div className="flex flex-col sm:flex-row gap-4">
          <HeroCard
            title={t.broadcasts}
            description={t.broadcastsDesc}
            to="/line-oa/broadcast/new"
            cta={t.sendBroadcast}
            icon={Megaphone}
          />
          <HeroCard
            title={t.campaigns}
            description={t.campaignsDesc}
            to="/line-oa/campaigns"
            cta={t.createCampaign}
            icon={Target}
          />
        </div>

        {/* Friends insights (Trends) */}
        <section className="card rounded-[5px] border border-black/[0.125] bg-white p-4">
          <div className="flex items-center">
            <h3 className="text-[1.05rem] font-bold text-[#353a40] flex-1">{t.friendsTitle}</h3>
            <Link to="/line-oa/audience" className="text-black/50 text-sm inline-flex items-center hover:text-[#353a40]">
              {t.audience} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Tabs (Trends only — Demographics/Friend path need LINE's demographic API) */}
          <div className="border-b border-black/10 mt-3 flex items-center justify-between">
            <div className="flex">
              <span className="px-4 py-2 text-sm font-semibold text-[#06C755] border-b-2 border-[#06C755] -mb-px">
                {t.trends}
              </span>
            </div>
            <div className="flex gap-1 pb-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`text-xs px-2 py-1 rounded ${
                    period === p.key ? 'bg-[#06C755]/10 text-[#06C755] font-semibold' : 'text-black/50 hover:bg-black/5'
                  }`}
                >
                  {t[p.tKey]}
                </button>
              ))}
            </div>
          </div>

          {loadingSpark ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : trendRows.length > 0 ? (
            <table className="w-full text-sm mt-3">
              <thead className="bg-[#f8f9fa] text-black/50">
                <tr>
                  <th className="text-left font-normal py-2 px-2 w-2/5">{t.metric}</th>
                  <th className="text-center font-normal py-2 px-2">{fmtDate(startDate || '')}</th>
                  <th className="text-center font-normal py-2 px-2">{fmtDate(endDate || '')}</th>
                  <th className="text-center font-normal py-2 px-2">{t.change}</th>
                  <th className="text-right font-normal py-2 px-2 w-1/5">{t.trend}</th>
                </tr>
              </thead>
              <tbody>
                {trendRows.map((r) => {
                  const first = r.series[0]?.value ?? 0
                  const last = r.series[r.series.length - 1]?.value ?? 0
                  return (
                    <tr key={r.label} className="border-b border-black/5 last:border-0">
                      <td className="py-2.5 px-2 text-[#353a40]">{r.label}</td>
                      <td className="py-2.5 px-2 text-center text-[#353a40]">{first.toLocaleString()}</td>
                      <td className="py-2.5 px-2 text-center text-[#353a40] font-semibold">{last.toLocaleString()}</td>
                      <td className="py-2.5 px-2 text-center"><Change delta={last - first} /></td>
                      <td className="py-2.5 px-2 w-24"><Sparkline points={r.series} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-black/50 py-8 text-center">
              {t.notEnoughData}
            </p>
          )}
        </section>

        {/* Broadcasts panel */}
        <section className="card rounded-[5px] border border-black/[0.125] bg-white p-4">
          <div className="flex items-center">
            <h3 className="text-[1.05rem] font-bold text-[#353a40] flex-1">{t.broadcasts}</h3>
            <Link to="/line-oa/broadcast" className="text-black/50 text-sm inline-flex items-center hover:text-[#353a40]">
              {t.yourBroadcasts} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Quota row */}
          <div className="flex items-center mt-3">
            <div className="flex-1 flex items-center">
              <span className="rounded-full border border-black/15 p-2 mr-2 flex items-center justify-center">
                <Radio className="w-4 h-4 text-[#353a40]" />
              </span>
              <span className="text-[1.4rem] font-bold text-[#353a40]">
                {quota ? quota.total_usage.toLocaleString() : '—'}
              </span>
              {quota && quota.quota_type === 'limited' && (
                <span className="text-black/50 text-sm ml-1 mt-1">/ {quota.quota_limit.toLocaleString()}</span>
              )}
              <span className="text-black/50 text-sm ml-2 mt-1">{t.messagesThisMonth}</span>
            </div>
            <Link
              to="/line-oa/broadcast/new"
              className="inline-block rounded-[5px] bg-[#06C755] hover:bg-[#06C755]/80 text-white font-semibold text-sm px-3 py-2 transition-colors"
            >
              {t.sendBroadcastShort}
            </Link>
          </div>

          {/* Past broadcasts — LINE-Manager style (thumbnail + preview · delivered · opens · clicks) */}
          {history && history.broadcasts.length > 0 ? (
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead className="bg-[#f8f9fa] text-black/50">
                  <tr>
                    <th className="text-left font-normal py-2 px-3">{t.pastBroadcast}</th>
                    <th className="text-center font-normal py-2 px-2 w-24">{t.delivered}</th>
                    <th className="text-center font-normal py-2 px-2 w-20">{t.opens}</th>
                    <th className="text-center font-normal py-2 px-2 w-24">{t.uniqueClicks}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.broadcasts.slice(0, 6).map((b, i) => {
                    const delivered = b.line_delivered ?? b.recipient_count
                    return (
                      <tr key={i} className="border-t border-black/5 hover:bg-black/[0.02]">
                        {/* Thumbnail + 2-line message + date */}
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                              {b.image_url ? (
                                <img src={b.image_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Megaphone className="w-4 h-4 text-black/30" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[#353a40] leading-snug line-clamp-2 max-w-[260px]">
                                {b.content_preview || <span className="text-black/40 italic">{t.imageMessage}</span>}
                              </p>
                              <p className="text-[11px] text-black/45 mt-0.5">
                                {b.sent_at
                                  ? new Date(b.sent_at).toLocaleString([], {
                                      year: 'numeric', month: '2-digit', day: '2-digit',
                                      hour: '2-digit', minute: '2-digit',
                                    })
                                  : '—'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {delivered != null ? delivered.toLocaleString() : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {b.line_opens != null ? b.line_opens.toLocaleString() : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {b.line_unique_clicks != null ? b.line_unique_clicks.toLocaleString() : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-black/40 mt-2 px-1">
                {t.opensClicksNote}
              </p>
            </div>
          ) : (
            <p className="text-sm text-black/50 py-6 text-center mt-2">
              {t.noBroadcasts}
            </p>
          )}
        </section>

        {/* Targeted campaigns — stats + send history (same data as the Campaigns page) */}
        <section className="card rounded-[5px] border border-black/[0.125] bg-white p-4">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-[1.05rem] font-bold text-[#353a40]">{t.campaignSectionTitle}</h3>
              <p className="text-xs text-black/45 mt-0.5">{t.campaignSectionDesc}</p>
            </div>
            <Link to="/line-oa/campaigns" className="text-black/50 text-sm inline-flex items-center hover:text-[#353a40]">
              {t.campaigns} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            {[
              { v: campaignTotalSends, l: t.campaignStatTotalSends },
              { v: campaignTotalDelivered, l: t.campaignStatTotalDelivered },
              { v: campaignSuccessful, l: t.campaignStatSuccessful },
            ].map((m) => (
              <div key={m.l} className="rounded-[8px] border border-black/[0.08] bg-[#f8f9fa] px-3 py-3">
                <p className="text-2xl font-bold text-[#353a40] tabular-nums">{m.v.toLocaleString()}</p>
                <p className="text-[13px] text-black/50 mt-1 leading-tight">{m.l}</p>
              </div>
            ))}
          </div>

          {/* Recent send history (reused component, capped) */}
          <div className="mt-4">
            <p className="text-sm font-semibold text-[#353a40] mb-2">{t.campaignHistoryTitle}</p>
            <CampaignSentHistory enabled={connected} limit={10} variant="plain" />
          </div>
        </section>

        {/* Automated notifications — per-type activity (last sent / this month / recent) */}
        <section className="card rounded-[5px] border border-black/[0.125] bg-white p-5">
          <div className="flex items-start gap-2.5 mb-4">
            <span className="w-8 h-8 rounded-[10px] bg-[#06C755]/10 flex items-center justify-center shrink-0 mt-0.5">
              <Bell className="w-4 h-4 text-[#06C755]" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[1.05rem] font-bold text-[#353a40] leading-tight">{t.automatedNotifications}</h3>
              <p className="text-xs text-black/45 mt-0.5">
                {t.automatedNotificationsDescPre}{' '}
                <Link to="/storefront/settings/line" className="text-[#06C755] font-medium hover:underline">{t.lineSettings}</Link>{t.automatedNotificationsDescPost}
              </p>
            </div>
          </div>
          <div className="space-y-0.5">
            {NOTIF_META.map(({ key, icon: Icon, enabledKey }) => {
              const a = notifActivity?.[key]
              const label = t[`notif_${key}`]
              const desc = t[`notif_${key}_desc`]
              const enabled = enabledKey ? !!status?.[enabledKey as keyof typeof status] : true
              const last = relTime(a?.last_sent, dfLocale)
              const recent = a?.recent?.[0]
              const total = a?.total ?? 0
              return (
                <div
                  key={key}
                  className="group flex items-center gap-3.5 rounded-xl px-2.5 py-2.5 -mx-1 transition-colors hover:bg-black/[0.025]"
                >
                  <span className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${enabled ? 'bg-[#06C755]/10 text-[#06C755]' : 'bg-black/[0.04] text-black/30'}`}>
                    <Icon className="w-[18px] h-[18px]" />
                    {enabled && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#06C755] ring-2 ring-white" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold truncate ${enabled ? 'text-[#353a40]' : 'text-black/45'}`}>{label}</p>
                      {!enabled && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-black/[0.05] text-black/40 shrink-0">
                          {t.off}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-black/45 truncate mt-0.5">
                      {last ? (
                        <>{t.lastSent} {last}{recent?.customer_name ? ` · ${recent.customer_name}` : ''}</>
                      ) : (
                        <span className="text-black/35">{desc}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0 pl-1">
                    <p className={`text-[17px] font-bold leading-none tabular-nums ${total > 0 ? 'text-[#353a40]' : 'text-black/25'}`}>
                      {total.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-black/40 mt-1">
                      {a && a.month > 0 ? `+${a.month} ${t.thisMonthShort}` : t.sent}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Payment reminders (real data) */}
        {reminderStats && reminderStats.reminders_sent > 0 && (
          <section className="card rounded-[5px] border border-black/[0.125] bg-white p-4">
            <h3 className="text-[1.05rem] font-bold text-[#353a40] mb-3">{t.paymentReminders}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { v: reminderStats.reminders_sent, l: t.remindersSent },
                { v: reminderStats.reminder_clicks, l: t.clicks },
                { v: reminderStats.reminder_conversions, l: t.paidAfter },
                { v: reminderStats.conversion_rate, l: t.conversionPct },
              ].map((m) => (
                <div key={m.l}>
                  <p className="text-2xl font-bold text-[#353a40]">
                    {m.v != null ? m.v.toLocaleString() : '—'}
                  </p>
                  <p className="text-[13px] text-black/50 mt-1">{m.l}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Secondary actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/line-oa/rich-menus" className="flex-1">
            <div className="card rounded-[5px] border border-black/[0.125] bg-white p-4 flex items-center gap-3 hover:shadow-sm transition-shadow">
              <span className="w-10 h-10 rounded-full bg-[#06C755]/10 flex items-center justify-center flex-shrink-0">
                <LayoutGrid className="w-5 h-5 text-[#06C755]" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#353a40]">{t.richMenus}</p>
                <p className="text-xs text-black/50">{t.richMenusDesc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-black/50" />
            </div>
          </Link>
          <Link to="/line-oa/audience" className="flex-1">
            <div className="card rounded-[5px] border border-black/[0.125] bg-white p-4 flex items-center gap-3 hover:shadow-sm transition-shadow">
              <span className="w-10 h-10 rounded-full bg-[#06C755]/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#06C755]" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#353a40]">{t.audience}</p>
                <p className="text-xs text-black/50">{t.audienceDesc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-black/50" />
            </div>
          </Link>
        </div>

        {loadingDash && (
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 rounded" />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  )
}
